import type {
  AgentAdoptionResponse,
  AgentAdoptionTeamAttribution,
  AgentAdoptionTeamRow,
  AgentAdoptionTierKey,
  ModelComparisonPeriod,
} from '../../../api/agent-miner-client/agent-miner-client.type';
import { STANDARD_DATE_FORMATS, formatDate } from '../../../helpers/date-time-helpers';
import { getTimeAllocationChartLabels } from '../../process-analysis/components/line-chart/line-chart.helpers';
import type { ChartDataItem } from '../../process-analysis/components/line-chart/line-chart.type';
import { deriveAllocationType } from '../model-comparison/model-comparison.helpers';
import type { AdoptionTrendMeasure } from './adoption.type';

/** Chart series key the Trends chart plots under. */
const ADOPTION_SERIES_KEY = 'adoption';

/** Maps each trend measure to its over_time series name on the payload. */
const MEASURE_SERIES_KEYS: Record<AdoptionTrendMeasure, 'active_users' | 'sessions' | 'adoption_rate'> = {
  active_users: 'active_users',
  sessions: 'sessions',
  adoption_rate: 'adoption_rate',
};

/** Compact `MMM D` axis/tooltip formatter shared by every period label. */
const formatPeriodLabel = (date: string): string => formatDate(date, { format: STANDARD_DATE_FORMATS.MONTH_DAY });

/**
 * True when the window has no sessions and no eligible engineers — the
 * tab-level empty state (a team filter can also produce this; the caller
 * distinguishes the message).
 *
 * @param {AgentAdoptionResponse} response - The adoption payload
 * @returns {boolean} Whether the population is empty
 */
const isAdoptionEmpty = (response: AgentAdoptionResponse): boolean =>
  response.summary.total_sessions === 0 && response.summary.eligible_users === 0;

/**
 * Maps the selected over_time series into the shared chart data shape: one
 * row per period. Nulls are preserved so the chart shows gaps for future
 * periods. Tick/tooltip labels are delegated to process-analysis's
 * `getTimeAllocationChartLabels`, matching the other Agent Miner charts.
 *
 * @param {AgentAdoptionResponse} response - The adoption payload
 * @param {AdoptionTrendMeasure} measure - Which series to plot
 * @returns {ChartDataItem[]} One chart row per period
 */
const buildAdoptionTrendData = (response: AgentAdoptionResponse, measure: AdoptionTrendMeasure): ChartDataItem[] => {
  const timeAllocationType =
    response.meta.time_allocation_type ?? deriveAllocationType(response.meta.start_date, response.meta.end_date);
  const series = response.over_time[MEASURE_SERIES_KEYS[measure]] ?? {};

  return response.meta.periods.map((period) => {
    const { tickLabel, tooltipLabel } = getTimeAllocationChartLabels({
      rawDate: period.id,
      timeAllocationType,
      timeAllocations: response.meta.periods,
      formatDateLabel: formatPeriodLabel,
    });
    return {
      date: tickLabel,
      rawDate: period.id,
      dateRange: tooltipLabel,
      [ADOPTION_SERIES_KEY]: series[period.id] ?? null,
    };
  });
};

/**
 * Orders a team row's per-period session counts by the response's period list
 * for the table-cell sparkline. Future-period nulls become zero — the
 * sparkline has no gap rendering and a trailing flatline reads correctly.
 *
 * @param {AgentAdoptionTeamRow} row - The team row
 * @param {ModelComparisonPeriod[]} periods - Chronological periods from meta
 * @returns {number[]} Per-period session counts
 */
const buildTeamSparklineData = (row: AgentAdoptionTeamRow, periods: ModelComparisonPeriod[]): number[] =>
  periods.map((period) => row.over_time.sessions?.[period.id] ?? 0);

/**
 * Computes each tier's share of a team's attributed sessions for the mix bar.
 * Shares are of the team's session total, so they sum to 100 for a fully
 * attributed team.
 *
 * @param {AgentAdoptionTeamRow} row - The team row
 * @returns {{ total: number, shares: Partial<Record<AgentAdoptionTierKey, number>> }} Total sessions and per-tier percentage shares
 */
const attributionShares = (
  row: AgentAdoptionTeamRow,
): { total: number; shares: Partial<Record<AgentAdoptionTierKey, number>> } => {
  const total = row.summary.sessions;
  if (total === 0) return { total, shares: {} };
  const shares: Partial<Record<AgentAdoptionTierKey, number>> = {};
  for (const [tier, count] of Object.entries(row.summary.attribution)) {
    shares[tier as AgentAdoptionTierKey] = (count / total) * 100;
  }
  return { total, shares };
};

/**
 * Share of a team's sessions attributed through any WORK signal (task link or
 * repository inference) — the trust headline for the scorecard's mix column.
 *
 * @param {AgentAdoptionTeamRow} row - The team row
 * @returns {number | null} Percentage of work-linked sessions, or null with no sessions
 */
const workLinkedShare = (row: AgentAdoptionTeamRow): number | null => {
  if (row.summary.sessions === 0) return null;
  const workLinkedCount =
    row.summary.attribution.task_linked +
    row.summary.attribution.branch_inferred +
    row.summary.attribution.directory_inferred;
  return (workLinkedCount / row.summary.sessions) * 100;
};

/**
 * Sessions per active user per week for one team, derived from the true
 * window width so the rate stays per-week at any bucket size.
 *
 * @param {AgentAdoptionTeamRow} row - The team row
 * @param {number} weeksInWindow - meta.weeks_in_window (never zero)
 * @returns {number | null} Sessions per user per week, or null with no active users
 */
const sessionsPerUserPerWeek = (row: AgentAdoptionTeamRow, weeksInWindow: number): number | null => {
  if (row.summary.active_agent_users === 0 || weeksInWindow <= 0) return null;
  return row.summary.sessions / row.summary.active_agent_users / weeksInWindow;
};

/**
 * One flattened Teams-scorecard table row. The shared Table component types
 * column keys as `keyof T`, so every displayed value — including derived ones
 * like the per-week rate and the work-linked share — must be a real property.
 */
interface AdoptionTeamTableRow {
  id: string;
  name: string;
  adoption_rate: number;
  active_agent_users: number;
  eligible_users: number;
  sessions: number;
  avg_turns_per_session: number | null;
  sessions_per_user_per_week: number | null;
  work_linked_pct: number | null;
  attribution: AgentAdoptionTeamAttribution;
  trend: number[];
}

/**
 * Flattens the payload's team rows into the table's view model, deriving the
 * per-week session rate, the work-linked share, and the sparkline series.
 *
 * @param {AgentAdoptionResponse} response - The adoption payload
 * @returns {AdoptionTeamTableRow[]} One flattened row per team
 */
const buildAdoptionTeamTableRows = (response: AgentAdoptionResponse): AdoptionTeamTableRow[] =>
  response.teams.map((row) => ({
    id: row.id,
    name: row.name,
    adoption_rate: row.summary.adoption_rate,
    active_agent_users: row.summary.active_agent_users,
    eligible_users: row.summary.eligible_users,
    sessions: row.summary.sessions,
    avg_turns_per_session: row.summary.avg_turns_per_session,
    sessions_per_user_per_week: sessionsPerUserPerWeek(row, response.meta.weeks_in_window),
    work_linked_pct: workLinkedShare(row),
    attribution: row.summary.attribution,
    trend: buildTeamSparklineData(row, response.meta.periods),
  }));

export {
  ADOPTION_SERIES_KEY,
  attributionShares,
  buildAdoptionTeamTableRows,
  buildAdoptionTrendData,
  buildTeamSparklineData,
  isAdoptionEmpty,
  sessionsPerUserPerWeek,
  workLinkedShare,
};
export type { AdoptionTeamTableRow };
